import { useState, useCallback, useMemo } from 'react';
import { Typography, Button, Table, Tag, Space, Row, Col, message, List, Card, Grid, Input, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { getLostAndFoundItems, deleteLostAndFoundItem } from '@/api/lostAndFound';
import { LostAndFoundItem } from '@/types/community/lostAndFound';
import { useAuth } from '@/contexts/AuthContext';
import LostAndFoundModal from '@/components/features/community/LostAndFoundModal';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';
import dayjs from 'dayjs';

const { Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const API_STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes

const itemStatusColors: Record<LostAndFoundItem['status'], string> = {
  found: 'blue',
  claimed: 'green',
  returned: 'purple',
  disposed: 'default'
};

const itemCategoryColors: Record<LostAndFoundItem['category'], string> = {
  electronics: 'blue',
  jewelry: 'gold',
  clothing: 'green',
  documents: 'purple',
  keys: 'orange',
  personal_items: 'cyan',
  other: 'default'
};

const itemConditionColors: Record<LostAndFoundItem['condition'], string> = {
  new: 'green',
  good: 'blue',
  fair: 'orange',
  poor: 'red',
  damaged: 'volcano',
  excellent: 'lime'
};

export default function LostAndFoundPage() {
  const { t } = useTranslation(['lostAndFound', 'common']);
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const screens = useBreakpoint();

  // State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState<LostAndFoundItem | null>(null);
  const [deleteError, setDeleteError] = useState<Error | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState('');

  // Queries
  const { data: itemsData, isLoading } = useQuery({
    queryKey: ['lostAndFound', firestoreUser?.workspace?.id],
    queryFn: () => getLostAndFoundItems({ 
      firestoreUser: firestoreUser!,
      pageSize: 100 // Get all items for this view
    }),
    enabled: !!firestoreUser?.workspace?.id,
    staleTime: API_STALE_TIME_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  // Mutations
  const deleteItemMutation = useMutation({
    mutationFn: (id: string) => deleteLostAndFoundItem(id, firestoreUser!),
    onSuccess: () => {
      message.success(t('lostAndFound:itemDeleted'));
      queryClient.invalidateQueries({ queryKey: ['lostAndFound'] });
      setIsDeleteModalVisible(false);
      setSelectedItem(null);
      setDeleteError(undefined);
    },
    onError: (error: any) => {
      setDeleteError(error);
    }
  });

  // Memoized values
  const itemList = useMemo(() => itemsData?.items || [], [itemsData]);

  // Utility functions
  const handleAddItem = useCallback(() => {
    setSelectedItem(null);
    setIsModalOpen(true);
  }, []);

  const handleEditItem = useCallback((item: LostAndFoundItem) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  }, []);

  const handleViewItem = useCallback((item: LostAndFoundItem) => {
    navigate(`/community/lost-and-found/${item.id}`, { 
      state: { 
        item,
        from: 'list'
      } 
    });
  }, [navigate]);

  const handleDeleteItem = useCallback((item: LostAndFoundItem) => {
    setSelectedItem(item);
    setDeleteError(undefined);
    setIsDeleteModalVisible(true);
  }, []);

  const handleModalSuccess = useCallback(() => {
    setIsModalOpen(false);
    setSelectedItem(null);
  }, []);

  const handleModalCancel = useCallback(() => {
    setIsModalOpen(false);
    setSelectedItem(null);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (selectedItem) {
      deleteItemMutation.mutate(selectedItem.id);
    }
  }, [selectedItem, deleteItemMutation]);

  const handleDeleteCancel = useCallback(() => {
    setIsDeleteModalVisible(false);
    setSelectedItem(null);
    setDeleteError(undefined);
  }, []);

  const handleErrorDismiss = useCallback(() => {
    setDeleteError(undefined);
  }, []);

  // Filter items
  const filteredItems = useMemo(() => {
    return itemList.filter(item => 
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.claimedBy?.name && item.claimedBy.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.reportedBy?.name && item.reportedBy.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.handedOverTo?.name && item.handedOverTo.name.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [itemList, searchTerm]);

  const columns = [
    {
      title: t('lostAndFound:table.title'),
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record: LostAndFoundItem) => (
        <Button type="link" onClick={() => handleViewItem(record)}>
          {text}
        </Button>
      ),
    },
    {
      title: t('lostAndFound:table.description'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: t('lostAndFound:table.category'),
      dataIndex: 'category',
      key: 'category',
      render: (category: LostAndFoundItem['category']) => (
        <Tag color={itemCategoryColors[category]}>
          {t(`lostAndFound:categories.${category}` as const, category)}
        </Tag>
      ),
    },
    {
      title: t('lostAndFound:table.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: LostAndFoundItem['status']) => (
        <Tag color={itemStatusColors[status]}>
          {t(`lostAndFound:statuses.${status}` as const, status)}
        </Tag>
      ),
    },
    {
      title: t('lostAndFound:table.condition'),
      dataIndex: 'condition',
      key: 'condition',
      render: (condition: LostAndFoundItem['condition']) => (
        <Tag color={itemConditionColors[condition]}>
          {t(`lostAndFound:conditions.${condition}` as const, condition)}
        </Tag>
      ),
    },
    {
      title: t('lostAndFound:table.foundAt'),
      dataIndex: 'foundAt',
      key: 'foundAt',
      render: (date: Date) => dayjs(date).format('YYYY-MM-DD HH:mm'),
      sorter: (a: LostAndFoundItem, b: LostAndFoundItem) => dayjs(a.foundAt).unix() - dayjs(b.foundAt).unix(),
      defaultSortOrder: 'descend' as const,
    },
    {
      title: t('common:comments'),
      key: 'comments',
      render: (_: any, record: LostAndFoundItem) => (
        <Text type="secondary">
          {record.comments?.length || 0} {t('common:comments')}
        </Text>
      ),
    },
    {
      title: t('common:actions.title'),
      key: 'actions',
      render: (_: any, record: LostAndFoundItem) => (
        <Space size="middle" wrap>
          <Button icon={<EditOutlined />} onClick={() => handleEditItem(record)} size={screens.xs ? 'small' : 'middle'}>
            {!screens.xs && t('common:actions.edit')}
          </Button>
          <Button icon={<DeleteOutlined />} danger onClick={() => handleDeleteItem(record)} size={screens.xs ? 'small' : 'middle'}>
            {!screens.xs && t('common:actions.delete')}
          </Button>
        </Space>
      ),
    },
  ];

  const renderItemCard = (item: LostAndFoundItem) => (
    <List.Item key={item.id}>
      <Card
        title={<Text strong>{item.title}</Text>}
        style={{ width: '100%' }}
        actions={[
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEditItem(item)} key="edit"> {t('common:actions.edit')}</Button>,
          <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDeleteItem(item)} key="delete"> {t('common:actions.delete')}</Button>,
        ]}
      >
        <Paragraph ellipsis={{ rows: 3, expandable: true, symbol: t('common:ui.more') }}>
          {item.description}
        </Paragraph>
        <Space direction="vertical" size="small" style={{ fontSize: '0.85em' }}>
          <Space wrap>
            <Tag color={itemCategoryColors[item.category]}>
              {t(`lostAndFound:categories.${item.category}` as const, item.category)}
            </Tag>
            <Tag color={itemStatusColors[item.status]}>
              {t(`lostAndFound:statuses.${item.status}` as const, item.status)}
            </Tag>
            <Tag color={itemConditionColors[item.condition]}>
              {t(`lostAndFound:conditions.${item.condition}` as const, item.condition)}
            </Tag>
          </Space>
          <Text type="secondary">{t('lostAndFound:table.foundAt')}: {dayjs(item.foundAt).format('YYYY-MM-DD HH:mm')}</Text>
          <Text type="secondary">{t('common:comments')}: {item.comments?.length || 0}</Text>
        </Space>
      </Card>
    </List.Item>
  );

  // Loading state
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: screens.xs ? '16px' : '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }} gutter={16}>
        <Col flex={1}>
           <Input.Search
            placeholder={t('searchPlaceholder', 'Search items by title or description...')}
            onSearch={setSearchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '100%' }}
          />
        </Col>
        <Col>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAddItem}
            block={screens.xs}
          >
            {t('lostAndFound:addItem')}
          </Button>
        </Col>
      </Row>

      {screens.xs ? (
        <List
          dataSource={filteredItems}
          renderItem={renderItemCard}
          grid={{ gutter: 16, column: 1 }}
        />
      ) : (
        <Table columns={columns} dataSource={filteredItems} rowKey="id" scroll={{ x: 'max-content' }} />
      )}

      {/* Lost and Found Modal */}
      <LostAndFoundModal
        open={isModalOpen}
        item={selectedItem}
        onSuccess={handleModalSuccess}
        onCancel={handleModalCancel}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        visible={isDeleteModalVisible}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('lostAndFound:confirmDeleteTitle')}
        error={deleteError}
        onErrorDismiss={handleErrorDismiss}
      />
    </div>
  );
} 